import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { StorageManager } from 'src/app/common/storage-manager.class';
import { GenerateEnvironmentDefinition } from '../environment-variables';
import { UserModel } from 'src/app/models/usuarios/user.model';

/**
 * Represents a single step displayed by the onboarding wizard.
 *
 * @export
 */
export interface WizardStep {
	/** Text displayed in the main action button for the step. */
	actionLabel: string;

	/** Optional image shown as visual support for the step. */
	imageUrl?: string;

	/** Secondary explanatory text displayed below the title. */
	subtitle: string;

	/** Main title displayed for the step. */
	title: string;
}

/**
 * Represents the user payload used to mark the wizard as completed.
 *
 * @internal
 */
interface WizardUser {
	/** User identifier required to build the update endpoint URL. */
	id: number | string;

	/** Local-only password field removed before saving. */
	password?: string;

	/** Local-only old password field removed before saving. */
	password_old?: string;

	/** Local-only repeated password field removed before saving. */
	password_repeat?: string;

	/** Indicates whether the onboarding wizard has been completed. */
	wizard_done?: number;

	/** Allows preserving additional user properties stored locally. */
	[key: string]: unknown;
}

const FIRST_STEP_INDEX = 0;
const AUTHORIZATION_STEP_INDEX = 1;
const WIZARD_DONE_VALUE = 1;

const AUTHORIZATION_DOCUMENT_URL =
	'https://locatec.es/proyectos/fitogest/documentos_consulta/guia-subida-autorizacion.pdf';

const SAVE_HEADERS = new HttpHeaders({
	'Content-Type': 'application/json',
	'Request-Method': 'PUT',
});

@Component({
	selector: 'app-wizard-modal',
	standalone: false,
	templateUrl: './wizard-modal.component.html',
	styleUrls: ['./wizard-modal.component.scss'],
})

/**
 * Displays the onboarding wizard modal and manages its navigation, validation and completion state.
 *
 * This component guides the user through the initial AgroGEST workflow, including
 * authorization document download, REA import information, treatment creation and
 * CUE submission guidance.
 *
 * @public
 *
 * @implements OnInit
 *
 * @example
 * ```html
 * <app-wizard-modal [(visible)]="showWizard"></app-wizard-modal>
 * ```
 *
 * @see WizardStep
 * @see StorageManager
 * @see GenerateEnvironmentDefinition
 * @see HttpClient
 */
export class WizardModalComponent implements OnInit {
	@Input() public visible = false;

	@Output() public readonly visibleChange = new EventEmitter<boolean>();

	public currentStepIndex = FIRST_STEP_INDEX;

	public authorizationDocumentDownloaded = true;

	public errorMessage = '';

	public isSaving = false;

	public readonly steps: ReadonlyArray<WizardStep> = [
		{
			actionLabel: 'Empezar recorrido',
			imageUrl: 'assets/images/wizard/step1.png',
			subtitle: 'Gestiona tu Cuaderno de Campo Digital paso a paso.',
			title: 'Bienvenido a AgroGEST',
		},
		{
			actionLabel: 'Descargar autorización',
			imageUrl: 'assets/images/wizard/step2.png',
			subtitle: 'Autoriza el acceso para consultar el REA o enviar el CUE.',
			title: 'Autoriza tus datos',
		},
		{
			actionLabel: 'Importar REA',
			imageUrl: 'assets/images/wizard/step3.png',
			subtitle: 'Carga tus explotaciones oficiales y empieza a trabajar al momento.',
			title: 'Importa tus explotaciones del REA',
		},
		{
			actionLabel: 'Crear tratamientos',
			imageUrl: 'assets/images/wizard/step4.png',
			subtitle: 'Registra tratamientos fitosanitarios y fertilizantes de forma sencilla.',
			title: 'Crea tratamientos',
		},
		{
			actionLabel: 'Enviar CUE',
			imageUrl: 'assets/images/wizard/step5.png',
			subtitle: 'Envía el cuaderno a la Administración y cumple la normativa.',
			title: 'Envía el Cuaderno Digital',
		},
	];

	private readonly environmentDefinition = new GenerateEnvironmentDefinition();

	private readonly httpClient = inject(HttpClient);

	/**
	 * Returns the currently active wizard step.
	 *
	 * @public
	 *
	 * @returns The active wizard step. If the current index is invalid, the first
	 * configured step is returned as fallback.
	 *
	 * @throws Error When no wizard steps are configured.
	 *
	 * @remarks
	 * The fallback prevents the template from receiving an undefined step when the
	 * current index is out of range.
	 */
	public get currentStep(): WizardStep {
		const firstStep = this.steps.at(FIRST_STEP_INDEX);

		if (firstStep == null) {
			throw new Error('No hay pasos configurados para el asistente.');
		}// if();

		return this.steps.at(this.currentStepIndex) ?? firstStep;
	}// ();

	/**
	 * Indicates whether the current wizard step is the first one.
	 *
	 * @public
	 *
	 * @returns `true` when the current step index matches the first step index;
	 * otherwise, `false`.
	 */
	public get isFirstStep(): boolean {
		return this.currentStepIndex === FIRST_STEP_INDEX;
	}// ();

	/**
	 * Indicates whether the current wizard step is the last one.
	 *
	 * @public
	 *
	 * @returns `true` when the current step index matches the last configured step;
	 * otherwise, `false`.
	 */
	public get isLastStep(): boolean {
		return this.currentStepIndex === this.steps.length - 1;
	}// ();

	/**
	 * Indicates whether the current wizard step is the authorization step.
	 *
	 * @public
	 *
	 * @returns `true` when the current step requires downloading the authorization
	 * document; otherwise, `false`.
	 */
	public get isAuthorizationStep(): boolean {
		return this.currentStepIndex === AUTHORIZATION_STEP_INDEX;
	}// ();

	/**
	 * Indicates whether the user can continue to the next wizard step.
	 *
	 * @public
	 *
	 * @returns `true` when the current step can be completed; otherwise, `false`.
	 *
	 * @remarks
	 * The authorization step requires the user to download the authorization
	 * document before continuing.
	 */
	public get canContinue(): boolean {
		return !this.isAuthorizationStep || this.authorizationDocumentDownloaded;
	}// ();

	/**
	 * Initializes the wizard modal component.
	 *
	 * This lifecycle hook preloads the configured step images to reduce visual
	 * loading delays while the user navigates through the wizard.
	 *
	 * @public
	 * @override
	 *
	 * @returns Nothing.
	 *
	 * @see preloadStepImages
	 */
	public ngOnInit(): void {
		this.preloadStepImages();
	}// ();

	/**
	 * Closes the wizard dialog.
	 *
	 * This method emits the visibility change event with `false` so the parent
	 * component can update the modal visibility binding.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @fires visibleChange
	 */
	public closeDialog(): void {
		this.visibleChange.emit(false);
	}// ();

	/**
	 * Moves the wizard to the previous step.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * If the current step is already the first one, the method exits without
	 * changing the current step index.
	 */
	public goToPreviousStep(): void {
		if (this.isFirstStep) {
			return;
		}// if();

		this.currentStepIndex -= 1;
	}// ();

	/**
	 * Moves the wizard to the next step or finishes it when the last step is reached.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * The method prevents navigation while saving or when the current step cannot
	 * continue. On the last step, the asynchronous save operation is started and its
	 * promise is intentionally ignored with `void`.
	 *
	 * @see canContinue
	 * @see finishWizard
	 */
	public goToNextStep(): void {
		if (!this.canContinue || this.isSaving) {
			return;
		}// if();

		if (this.isLastStep) {
			void this.finishWizard();
			return;
		}// if();

		this.currentStepIndex += 1;
	}// ();

	/**
	 * Opens the authorization document in a new browser tab.
	 *
	 * This method opens the configured authorization guide, marks the authorization
	 * document as downloaded and clears previous error messages.
	 *
	 * @public
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * If the browser blocks the new window, the method displays an understandable
	 * error message and does not mark the document as downloaded.
	 *
	 * The `opener` reference is removed to avoid giving the opened page access to
	 * the current window.
	 *
	 * @see AUTHORIZATION_DOCUMENT_URL
	 */
	public downloadAuthorizationDocument(): void {
		const openedWindow = window.open(AUTHORIZATION_DOCUMENT_URL, '_blank');

		if (openedWindow == null) {
			this.errorMessage = 'No se ha podido abrir el documento. Revisa el bloqueador de ventanas.';
			return;
		}// if();

		openedWindow.opener = null;

		this.authorizationDocumentDownloaded = true;
		this.errorMessage = '';
	}// ();

	/**
	 * Marks the wizard as completed for the current user and closes the dialog.
	 *
	 * This method builds the sanitized user payload, sends the update request to the
	 * backend, stores the updated user locally, resets the wizard state and notifies
	 * the parent component to close the modal.
	 *
	 * @private
	 *
	 * @returns A promise resolved when the wizard completion flow finishes.
	 *
	 * @remarks
	 * The saving state is always reset in the `finally` block, even when the request
	 * fails. User-facing errors are normalized through `getErrorMessage`.
	 *
	 * @throws Error When the current user cannot be identified.
	 *
	 * @see getWizardDoneUser
	 * @see getSaveUrl
	 * @see getErrorMessage
	 * @see StorageManager.saveUser
	 */
	private async finishWizard(): Promise<void> {
		this.isSaving = true;
		this.errorMessage = '';

		try {
			const user = this.getWizardDoneUser();

			await firstValueFrom(this.httpClient.put<void>(this.getSaveUrl(user.id), user, { headers: SAVE_HEADERS }));

			StorageManager.saveUser(user as UserModel);

			this.resetWizard();
			this.visibleChange.emit(false);
		} catch (error: unknown) {
			this.errorMessage = this.getErrorMessage(error);
		} finally {
			this.isSaving = false;
		}// try/catch
	}// ();

	/**
	 * Builds the user payload used to persist wizard completion.
	 *
	 * @private
	 *
	 * @returns The current stored user with `wizard_done` enabled and local-only
	 * password fields removed.
	 *
	 * @throws Error When there is no stored user identifier.
	 *
	 * @remarks
	 * Password-related fields are removed before saving because they are local-only
	 * values and must not be sent as part of the wizard completion update.
	 *
	 * @see WizardUser
	 * @see StorageManager.getUser
	 */
	private getWizardDoneUser(): WizardUser {
		const storedUser = StorageManager.getUser() as Partial<WizardUser> | null;

		if (storedUser?.id == null) {
			throw new Error('No se ha podido identificar el usuario.');
		}// if();

		const user: WizardUser = {
			...storedUser,
			id: storedUser.id,
			wizard_done: WIZARD_DONE_VALUE,
		};

		// Remove local-only fields before saving.
		delete user.password;
		delete user.password_old;
		delete user.password_repeat;

		return user;
	}// ();

	/**
	 * Builds the backend URL used to save the wizard completion state.
	 *
	 * @private
	 *
	 * @param userId - Identifier of the user being updated.
	 *
	 * @returns The complete endpoint URL for the user update request.
	 *
	 * @see GenerateEnvironmentDefinition.serverUrl
	 */
	private getSaveUrl(userId: WizardUser['id']): string {
		return `${this.environmentDefinition.serverUrl}ws/index.php?p1=generic&p2=usuarios&p3=${userId}`;
	}// ();

	/**
	 * Preloads all configured wizard step images.
	 *
	 * @private
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * Steps without an image URL are ignored. The created `Image` instances are not
	 * stored because assigning the `src` is enough to let the browser cache them.
	 */
	private preloadStepImages(): void {
		for (const step of this.steps) {
			if (step.imageUrl == null) {
				continue;
			}// if();s

			const image = new Image();

			image.src = step.imageUrl;
		}// for();
	}// ();

	/**
	 * Restores the wizard to its initial state.
	 *
	 * @private
	 *
	 * @returns Nothing.
	 *
	 * @remarks
	 * This method resets the current step, authorization download flag and visible
	 * error message.
	 */
	private resetWizard(): void {
		this.currentStepIndex = FIRST_STEP_INDEX;
		this.authorizationDocumentDownloaded = false;
		this.errorMessage = '';
	}// ();

	/**
	 * Resolves a user-facing error message from an unknown error value.
	 *
	 * @private
	 *
	 * @param error - Error value caught during the wizard completion flow.
	 *
	 * @returns A readable error message suitable for displaying in the template.
	 *
	 * @remarks
	 * HTTP errors try to reuse the backend `message` field when available. Standard
	 * JavaScript errors return their message. Unknown error shapes return a generic
	 * fallback message.
	 *
	 * @see HttpErrorResponse
	 * @see getServerErrorMessage
	 */
	private getErrorMessage(error: unknown): string {
		if (error instanceof HttpErrorResponse) {
			return this.getServerErrorMessage(error.error) ?? 'No se ha podido guardar el asistente. Inténtalo de nuevo.';
		}// if();

		if (error instanceof Error) {
			return error.message;
		}// if();

		return 'Ha ocurrido un error inesperado.';
	}// ();

	/**
	 * Extracts a backend error message from an unknown response payload.
	 *
	 * @private
	 *
	 * @param response - Backend response payload received from an HTTP error.
	 *
	 * @returns The backend message when it exists and is a string; otherwise, `null`.
	 *
	 * @remarks
	 * The method validates the response shape before reading the `message` property
	 * to remain compatible with strict TypeScript mode.
	 */
	private getServerErrorMessage(response: unknown): string | null {
		if (typeof response !== 'object' || response == null || !('message' in response)) {
			return null;
		}// if();

		const message = (response as { message?: unknown }).message;

		return typeof message === 'string' ? message : null;
	}// ();
}// class;
